import React, { useState, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Divider,
  Grid,
  Paper,
  IconButton,
  useTheme
} from '@mui/material';
import {
  Close as CloseIcon,
  Print as PrintIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';
import { useReactToPrint } from 'react-to-print';
import JsBarcode from 'jsbarcode';
import { getCompanyInfo } from '../../../services/migration/companyDataService';
import { useTranslation } from 'react-i18next';

interface N11PrintModalProps {
  open: boolean;
  onClose: () => void;
  order: any;
  companyId?: string; // Şirket bilgilerini çekmek için
}

interface CompanyInfo {
  companyName?: string;
  phone?: string;
}

const N11PrintModal: React.FC<N11PrintModalProps> = ({ open, onClose, order, companyId }) => {
  const theme = useTheme();
  const { t } = useTranslation('salesSystem');
  const printRef = useRef<HTMLDivElement>(null);
  const [isPreview, setIsPreview] = useState(true);
  const [companyInfo, setCompanyInfo] = useState<CompanyInfo>({});

  // Şirket bilgilerini Firebase'den çek
  useEffect(() => {
    const fetchCompanyInfo = async () => {
      if (!companyId || !open) return;

      try {
        const info = await getCompanyInfo(companyId);
        if (info) {
          setCompanyInfo({
            companyName: info.companyName,
            phone: info.phone
          });
        }
      } catch (error) {
        console.error('[N11 PRINT MODAL] Şirket bilgileri çekilirken hata:', error);
      }
    };

    fetchCompanyInfo();
  }, [companyId, open]);

  // Barkod oluşturma fonksiyonu
  const generateBarcode = (text: string) => {
    const canvas = document.createElement('canvas');
    try {
      JsBarcode(canvas, text, {
        format: "CODE128",
        width: 3,
        height: 80,
        displayValue: true,
        fontSize: 14,
        margin: 5
      });
      return canvas.toDataURL();
    } catch (error) {
      console.error('Barkod oluşturma hatası:', error);
      return '';
    }
  };

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: `N11_Siparis_${order?.id || t('ecommerceOrders.marketplaces.unknown')}`,
    onBeforeGetContent: () => {
      setIsPreview(false);
      return Promise.resolve();
    },
    onAfterPrint: () => {
      setIsPreview(true);
    }
  });

  if (!order) return null;

  // Debug: Firebase'den gelen sipariş verisini logla
  console.log('🔍 [N11 PRINT MODAL] Firebase\'den gelen sipariş verisi:', order);
  console.log('📍 [N11 PRINT MODAL] shippingAddress:', order.shippingAddress);
  console.log('📍 [N11 PRINT MODAL] billingAddress:', order.billingAddress);
  console.log('👤 [N11 PRINT MODAL] customerName:', order.customerName);

  const cargoBarcodeValue =
    order.cargoInfo?.cargoBarcode ||
    order.cargoInfo?.cargoSenderNumber ||
    order.cargoBarcode ||
    order.cargoInfo?.cargoTrackingNumber ||
    order.trackingNumber ||
    order.n11OrderId ||
    order.orderNumber ||
    order.id ||
    '';
  const barcodeDataUrl = cargoBarcodeValue ? generateBarcode(cargoBarcodeValue) : '';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: '80vh',
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        bgcolor: '#7b1fa2',
        color: 'white'
      }}>
        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
          <PrintIcon sx={{ mr: 1 }} />
          {t('ecommerceOrders.n11PrintModal.title')}
        </Typography>
        <IconButton onClick={onClose} sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Box ref={printRef}>
          {/* Önizleme/Yazdırma İçeriği */}
          <Box sx={{ 
            p: isPreview ? 2 : 0,
            bgcolor: 'white',
            minHeight: isPreview ? 'auto' : 'auto',
            '@media print': {
              p: '5mm',
              m: 0,
              bgcolor: 'white !important',
              color: 'black !important',
              minHeight: 'auto !important',
              height: 'auto !important',
              pageBreakInside: 'auto',
              fontSize: '8pt !important',
              '@page': {
                size: 'A5',
                margin: '5mm'
              },
              '& .page-break-avoid': {
                pageBreakInside: 'avoid'
              },
              '& .page-break-before': {
                pageBreakBefore: 'always'
              },
              '& *': {
                fontSize: '8pt !important'
              }
            }
          }}>
            {/* Logo - Üst Orta */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center',
              mb: 0,
              '@media print': {
                mb: 0
              }
            }}>
              <img 
                src="/assets/logoHighResNew.png" 
                alt="Logo"
                style={{ 
                  height: isPreview ? '30px' : '25px',
                  width: 'auto'
                }}
              />
            </Box>

            {/* Kargo Kampanya Kodu - Gönderici Bilgilerinin Üstünde */}
            <Paper className="page-break-avoid" sx={{ 
              mb: 0, 
              mt: 0,
              p: 1, 
              border: '2px solid #7b1fa2',
              borderRadius: isPreview ? 1 : 0,
              '@media print': {
                borderRadius: 0,
                boxShadow: 'none'
              }
            }}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1
              }}>
                {/* Sol taraf - Barkod */}
                <Box sx={{ flex: '0 0 auto' }}>
                  {barcodeDataUrl && (
                    <img
                      src={barcodeDataUrl}
                      alt={t('ecommerceOrders.n11PrintModal.cargoBarcodeAlt')}
                      style={{ 
                        height: isPreview ? '70px' : '60px',
                        width: 'auto'
                      }}
                    />
                  )}
                </Box>
                
                {/* Sağ taraf - Uyarı metni */}
                <Box sx={{ flex: 1, textAlign: 'right' }}>
                  <Typography variant="caption" sx={{ 
                    fontSize: '0.6rem',
                    lineHeight: 1.2,
                    display: 'block'
                  }}>
                    {t('ecommerceOrders.n11PrintModal.attachCargoLabel')}
                  </Typography>
                  <Typography variant="caption" sx={{ 
                    fontSize: '0.6rem',
                    lineHeight: 1.2,
                    display: 'block'
                  }}>
                    {t('ecommerceOrders.n11PrintModal.trackShipmentNote')}
                  </Typography>
                </Box>
              </Box>
            </Paper>

            {/* Gönderici Bilgileri - Alt Alta Düzen */}
            <Paper className="page-break-avoid" sx={{ 
              mb: 0, 
              p: 1, 
              border: '1px solid #7b1fa2',
              borderRadius: isPreview ? 1 : 0,
              '@media print': {
                borderRadius: 0,
                boxShadow: 'none'
              }
            }}>
              <Typography variant="subtitle1" sx={{ 
                fontWeight: 'bold', 
                mb: 0.5,
                color: '#7b1fa2',
                fontSize: '0.7rem'
              }}>
                {t('ecommerceOrders.n11PrintModal.senderInformation')}
              </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                  <Typography variant="caption" sx={{ fontSize: '0.6rem', minWidth: '60px', fontWeight: 'medium' }}>
                    {t('ecommerceOrders.n11PrintModal.companyName')}:
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.6rem', ml: 1 }}>
                    {companyInfo.companyName || t('ecommerceOrders.n11PrintModal.loadingCompanyName')}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                  <Typography variant="caption" sx={{ fontSize: '0.6rem', minWidth: '60px', fontWeight: 'medium' }}>
                    {t('ecommerceOrders.n11PrintModal.companyPhone')}:
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.6rem', ml: 1 }}>
                    {companyInfo.phone || t('ecommerceOrders.n11PrintModal.loadingPhone')}
                  </Typography>
                </Box>
              </Box>
            </Paper>

            {/* Alıcı Bilgileri - Alt Alta Düzen */}
            <Paper className="page-break-avoid" sx={{ 
              mb: 0, 
              p: 1, 
              border: '1px solid #7b1fa2',
              borderRadius: isPreview ? 1 : 0,
              '@media print': {
                borderRadius: 0,
                boxShadow: 'none'
              }
            }}>
              <Typography variant="subtitle1" sx={{ 
                fontWeight: 'bold', 
                mb: 0.5,
                color: '#7b1fa2',
                fontSize: '0.7rem'
              }}>
                {t('ecommerceOrders.n11PrintModal.recipientInformation')}
              </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                  <Typography variant="caption" sx={{ fontSize: '0.6rem', minWidth: '80px', fontWeight: 'medium' }}>
                    {t('ecommerceOrders.n11PrintModal.fullName')}:
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.6rem', ml: 1 }}>
                    {order.shippingAddress?.fullName || order.customerName || t('ecommerceOrders.table.customerNameUnknown')}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                  <Typography variant="caption" sx={{ fontSize: '0.6rem', minWidth: '80px', fontWeight: 'medium' }}>
                    {t('ecommerceOrders.n11PrintModal.address')}:
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.6rem', ml: 1, lineHeight: 1.2 }}>
                    {order.shippingAddress?.address || t('ecommerceOrders.n11PrintModal.addressNotFound')}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                  <Typography variant="caption" sx={{ fontSize: '0.6rem', minWidth: '80px', fontWeight: 'medium' }}>
                    {t('ecommerceOrders.n11PrintModal.district')}:
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.6rem', ml: 1 }}>
                    {order.shippingAddress?.district || t('ecommerceOrders.n11PrintModal.districtUnavailable')}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                  <Typography variant="caption" sx={{ fontSize: '0.6rem', minWidth: '80px', fontWeight: 'medium' }}>
                    {t('ecommerceOrders.n11PrintModal.city')}:
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.6rem', ml: 1 }}>
                    {order.shippingAddress?.city || t('ecommerceOrders.n11PrintModal.cityUnavailable')}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                  <Typography variant="caption" sx={{ fontSize: '0.6rem', minWidth: '80px', fontWeight: 'medium' }}>
                    {t('ecommerceOrders.orderDetailsModal.postalCode')}:
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.6rem', ml: 1 }}>
                    {order.shippingAddress?.postalCode || t('ecommerceOrders.n11PrintModal.noPostalCode')}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                  <Typography variant="caption" sx={{ fontSize: '0.6rem', minWidth: '80px', fontWeight: 'medium' }}>
                    {t('ecommerceOrders.n11PrintModal.homeMobilePhone')}:
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.6rem', ml: 1 }}>
                    {order.shippingAddress?.gsm || order.customerPhone || t('ecommerceOrders.n11PrintModal.phoneUnavailable')}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                  <Typography variant="caption" sx={{ fontSize: '0.7rem', minWidth: '120px', fontWeight: 'medium' }}>
                    {t('ecommerceOrders.n11PrintModal.paymentType')}:
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.75rem', ml: 1, color: '#7b1fa2' }}>
                    {t('ecommerceOrders.n11PrintModal.paidByN11')}
                  </Typography>
                </Box>
              </Box>
            </Paper>

            {/* Sipariş Bilgileri - Alt Alta Düzen */}
            <Paper className="page-break-avoid" sx={{ 
              mb: 0, 
              p: 1, 
              border: '1px solid #7b1fa2',
              borderRadius: isPreview ? 1 : 0,
              '@media print': {
                borderRadius: 0,
                boxShadow: 'none'
              }
            }}>
              <Typography variant="subtitle1" sx={{ 
                fontWeight: 'bold', 
                mb: 0.5,
                color: '#7b1fa2',
                fontSize: '0.7rem'
              }}>
                {t('ecommerceOrders.orderDetailsModal.orderInformation')}
              </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                  <Typography variant="caption" sx={{ fontSize: '0.6rem', minWidth: '80px', fontWeight: 'medium' }}>
                    {t('ecommerceOrders.n11PrintModal.orderNumber')}:
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.6rem', ml: 1 }}>
                    {order.n11OrderId || order.id}
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                  <Typography variant="caption" sx={{ fontSize: '0.6rem', minWidth: '80px', fontWeight: 'medium' }}>
                    {t('ecommerceOrders.n11OrderDetailsModal.cargoCompany')}:
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.6rem', ml: 1 }}>
                    {order.cargoInfo?.cargoProviderName || t('ecommerceOrders.n11PrintModal.defaultCargoCompany')}
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                  <Typography variant="caption" sx={{ fontSize: '0.7rem', minWidth: '120px', fontWeight: 'medium' }}>
                    {t('ecommerceOrders.n11PrintModal.paymentType')}:
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.75rem', ml: 1, color: '#7b1fa2' }}>
                    {t('ecommerceOrders.n11PrintModal.paidByN11')}
                  </Typography>
                </Box>
              </Box>
            </Paper>

            {/* Ürün Listesi - Alt Alta Düzen */}
            <Paper sx={{ 
              mb: 0, 
              p: 1, 
              border: '1px solid #7b1fa2',
              borderRadius: isPreview ? 1 : 0,
              '@media print': {
                borderRadius: 0,
                boxShadow: 'none'
              }
            }}>
              <Typography variant="subtitle1" sx={{ 
                fontWeight: 'bold', 
                mb: 0.5,
                color: '#7b1fa2',
                fontSize: '0.7rem'
              }}>
                {t('ecommerceOrders.n11PrintModal.productList')}
              </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
                {order.items?.map((item: any, index: number) => (
                  <Box key={index} sx={{ 
                    display: 'flex', 
                    alignItems: 'flex-start',
                    borderBottom: index < order.items.length - 1 ? '1px solid #eee' : 'none',
                    pb: index < order.items.length - 1 ? 0.3 : 0
                  }}>
                    <Typography variant="caption" sx={{ fontSize: '0.6rem', minWidth: '60px', fontWeight: 'medium' }}>
                      {t('ecommerceOrders.n11PrintModal.productNameQuantity')}:
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: '0.6rem', ml: 1, lineHeight: 1.2 }}>
                      {item.name} / {item.quantity}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Paper>


          </Box>
        </Box>
      </DialogContent>

      {isPreview && (
        <DialogActions sx={{ p: 2, bgcolor: '#f5f5f5' }}>
          <Button
            onClick={onClose}
            variant="outlined"
            startIcon={<CloseIcon />}
          >
            {t('ecommerceOrders.orderDetailsModal.close')}
          </Button>
          <Button
            onClick={handlePrint}
            variant="contained"
            startIcon={<PrintIcon />}
            sx={{ 
              bgcolor: '#7b1fa2',
              '&:hover': { bgcolor: '#6a1b9a' }
            }}
          >
            {t('ecommerceOrders.n11PrintModal.print')}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default N11PrintModal;
